import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { FC, useState } from 'react';
import { SeriesData } from '~/utils/transforms/patientDataTransforms';
import { ActiveItem } from '../../useActiveItems';
import { SelectedDatasets } from '../LoadedPatientData';
import PatientDatasetRow from './DatasetRow';

type SortField = 'modality' | 'description' | 'seriesDate' | 'id';
type SortDirection = 'asc' | 'desc';

interface PatientDataTableProps {
    rows: SeriesData[];
    selectedDatasets: SelectedDatasets;
    activeItems: ActiveItem[];
    onCheckSelect: (
        event: React.MouseEvent<HTMLElement, MouseEvent>,
        serie: SeriesData
    ) => void;
}

const PatientDataTable: FC<PatientDataTableProps> = ({
    rows,
    selectedDatasets,
    onCheckSelect,
    activeItems,
}) => {
    const [sortField, setSortField] = useState<SortField>('seriesDate');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    const handleSort = (field: SortField) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const sortedRows = [...rows].sort((a, b) => {
        let aValue: Date | string | number | undefined = a[sortField];
        let bValue: Date | string | number | undefined = b[sortField];

        // Handle date sorting
        if (sortField === 'seriesDate') {
            aValue = aValue ? new Date(aValue).getTime() : 0;
            bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        // Handle string sorting
        if (typeof aValue === 'string' && typeof bValue === 'string') {
            aValue = aValue.toLowerCase();
            bValue = bValue.toLowerCase();
        }

        const aComp = aValue !== undefined ? aValue : '';
        const bComp = bValue !== undefined ? bValue : '';
        if (aComp < bComp) return sortDirection === 'asc' ? -1 : 1;
        if (aComp > bComp) return sortDirection === 'asc' ? 1 : -1;
        return 0;
    });

    // Move active items to top
    const finalRows = sortedRows.sort((row) => {
        return activeItems.some((item) => item.dicomUid === row.id) ? -1 : 1;
    });

    return (
        <TableContainer>
            <Table
                size="small"
                sx={{
                    '& .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ width: '10px', py: 0.5 }}></TableCell>
                        <TableCell sx={{ width: '80px', px: 1, py: 0.5 }}>
                            <TableSortLabel
                                active={sortField === 'modality'}
                                direction={
                                    sortField === 'modality'
                                        ? sortDirection
                                        : 'asc'
                                }
                                onClick={() => handleSort('modality')}
                            >
                                Modality
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: 'auto', px: 1, py: 0.5 }}>
                            <TableSortLabel
                                active={sortField === 'description'}
                                direction={
                                    sortField === 'description'
                                        ? sortDirection
                                        : 'asc'
                                }
                                onClick={() => handleSort('description')}
                            >
                                Description
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: '120px', px: 1, py: 0.5 }}>
                            <TableSortLabel
                                active={sortField === 'seriesDate'}
                                direction={
                                    sortField === 'seriesDate'
                                        ? sortDirection
                                        : 'asc'
                                }
                                onClick={() => handleSort('seriesDate')}
                            >
                                Date
                            </TableSortLabel>
                        </TableCell>
                        <TableCell sx={{ width: '15%', px: 1, py: 0.5 }}>
                            <TableSortLabel
                                active={sortField === 'id'}
                                direction={
                                    sortField === 'id' ? sortDirection : 'asc'
                                }
                                onClick={() => handleSort('id')}
                            >
                                UID
                            </TableSortLabel>
                        </TableCell>
                        <TableCell
                            sx={{ width: '100px', px: 1, py: 0.5 }}
                        ></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {finalRows.map((row) => (
                        <PatientDatasetRow
                            key={row.id}
                            row={row}
                            selectedDatasets={selectedDatasets}
                            onCheckSelect={onCheckSelect}
                            activeItems={activeItems}
                        />
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default PatientDataTable;
